import {
  Box,
  Chip,
  FormHelperText,
  Input,
  MenuItem,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import Scrollbar from 'src/components/scrollbar';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import { useOnClickOutside } from 'usehooks-ts';
import { useFormContext } from 'react-hook-form';

const LANGUAGES = [
  {
    label: 'Java',
    value: 'JAVA',
  },
  {
    label: 'Python',
    value: 'PYTHON',
  },
  {
    label: 'C',
    value: 'C',
  },
  {
    label: 'C++',
    value: 'CPP',
  },
  {
    label: 'JavaScript',
    value: 'JAVASCRIPT',
  },
  {
    label: 'Ruby',
    value: 'RUBY',
  },
];

export default function Languages({ methods, parentFieldName }) {
  // refs
  const addLanguageInputRef = useRef(null);
  const languagesPopoverRef = useRef(null);

  // hooks
  const {
    formState: { errors },
  } = useFormContext();

  // states
  const [addNewLanguage, setAddNewLanguage] = useState(false);
  const [newLanguage, setNewLanguage] = useState('');
  const [languageInputWidth, setLanguageInputWidth] = useState('100px');
  const [showLanguagesDropdown, setShowLanguagesDropdown] = useState(false);

  const languagesFieldName = `${parentFieldName}.languages`;
  const languages = methods.watch(languagesFieldName);

  // functions
  const handleLanguageInputChange = event => {
    const inputValue = event.target.value;
    setNewLanguage(inputValue);

    if (
      LANGUAGES.some(
        language =>
          language.label.toLowerCase().includes(inputValue.toLowerCase()) &&
          !languages.includes(language.label),
      )
    ) {
      setShowLanguagesDropdown(true);
    } else {
      setShowLanguagesDropdown(false);
    }

    const newWidth = `${Math.max(24, inputValue.length * 8)}px`;
    setLanguageInputWidth(newWidth);
  };

  const handleEnterKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  useOnClickOutside(languagesPopoverRef, () => {
    setShowLanguagesDropdown(false);
  });

  useOnClickOutside(addLanguageInputRef, () => {
    setAddNewLanguage(false);
    setNewLanguage('');
  });

  return (
    <Stack spacing="12px">
      <Typography variant="subheading2" fontSize={16}>
        Coding Languages
      </Typography>
      <Stack>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Box
            ref={addLanguageInputRef}
            onFocus={() => setShowLanguagesDropdown(true)}
          >
            <Chip
              icon={!addNewLanguage && <Iconify icon="tabler:plus" />}
              sx={{
                width: addNewLanguage ? 'auto' : '150px',
                borderColor: theme => theme.palette.content.primaryBlack1,
              }}
              variant="outlined"
              label={
                addNewLanguage ? (
                  <Input
                    value={newLanguage}
                    autoFocus
                    onChange={handleLanguageInputChange}
                    onKeyDown={handleEnterKeyPress}
                    onClick={() => {
                      if (!showLanguagesDropdown)
                        setShowLanguagesDropdown(true);
                    }}
                    disableUnderline
                    style={{
                      background: 'transparent',
                      padding: 0,
                      margin: 0,
                      minWidth: '68px',
                      width: languageInputWidth,
                    }}
                  />
                ) : (
                  'Add Language'
                )
              }
              clickable={!addNewLanguage}
              onClick={event => {
                if (!addNewLanguage) setAddNewLanguage(true);
              }}
            />
            <Popper
              ref={languagesPopoverRef}
              open={showLanguagesDropdown}
              anchorEl={addLanguageInputRef.current}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              sx={{
                pt: '10px',
              }}
            >
              <Paper
                sx={{
                  border: theme =>
                    `1px solid ${theme.palette.border.primaryBlack1}`,
                }}
              >
                <Scrollbar sx={{ width: 160, height: 200, overflow: 'auto' }}>
                  {LANGUAGES.filter(
                    language =>
                      language.label
                        .toLowerCase()
                        .includes(newLanguage.toLowerCase()) &&
                      !languages.includes(language.value),
                  ).map(language => (
                    <MenuItem
                      key={language.value}
                      onClick={() => {
                        methods.setValue(languagesFieldName, [
                          ...languages,
                          language.value,
                        ]);
                        setAddNewLanguage(false);
                        setNewLanguage('');
                        setShowLanguagesDropdown(false);
                      }}
                    >
                      {language.label}
                    </MenuItem>
                  ))}
                </Scrollbar>
              </Paper>
            </Popper>
          </Box>

          {languages.map(language => (
            <Chip
              key={language}
              label={
                LANGUAGES.find(_language => _language.value === language)
                  ?.label || language
              }
              clickable
              variant="outlined"
              onDelete={() => {
                const _languages = languages.filter(
                  _language => _language !== language,
                );

                methods.setValue(languagesFieldName, _languages);
              }}
            />
          ))}
        </Stack>
        {errors?.codingAssessments?.map((assessment, index) => (
          <FormHelperText
            key={index}
            error={!!assessment?.languages}
            sx={{ px: 2 }}
          >
            {assessment?.languages?.message}
          </FormHelperText>
        ))}
      </Stack>
    </Stack>
  );
}

Languages.propTypes = {
  methods: PropTypes.object.isRequired,
  parentFieldName: PropTypes.string.isRequired,
};
